import React, { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

export type Notification_Type = 'success' | 'danger' | 'info' | 'default' | 'warning';

type NotificationContainer = 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center' | 'center';

export interface NotificationDismissOptions {
    duration?: number;
    click?: boolean;
    touch?: boolean;
    onScreen?: boolean;
    pauseOnHover?: boolean;
    waitForAnimation?: boolean;
}

export interface NotificationSettings {
    id?: string;
    type?: Notification_Type;
    title?: ReactNode;
    message?: ReactNode;
    description?: ReactNode;
    content?: ReactNode;
    dismiss?: NotificationDismissOptions;
    container?: NotificationContainer;
    insert?: 'top' | 'bottom';
    animationIn?: string[];
    animationOut?: string[];
    width?: number | string;
    onRemoval?: (id: string, removedBy: string) => void;
}

interface NotificationItem extends NotificationSettings {
    id: string;
    type: Notification_Type;
    container: NotificationContainer;
}

type NotificationListener = (notifications: NotificationItem[]) => void;

const notificationListeners = new Set<NotificationListener>();
const notificationTimers = new Map<string, ReturnType<typeof setTimeout>>();
let notifications: NotificationItem[] = [];

const notificationsPortal = document.createElement('div');
notificationsPortal.id = 'notifications-portal';
document.body.appendChild(notificationsPortal);

const createNotificationId = () => `notification-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const emitNotifications = () => {
    notificationListeners.forEach((listener) => listener(notifications));
};

const clearNotificationTimer = (id: string) => {
    const timer = notificationTimers.get(id);
    if (!timer) return;

    clearTimeout(timer);
    notificationTimers.delete(id);
};

const getNotificationDuration = (settings: NotificationSettings) => settings.dismiss?.duration;

const setNotificationTimer = (notification: NotificationItem) => {
    const duration = getNotificationDuration(notification);
    if (duration == null || duration <= 0) return;

    clearNotificationTimer(notification.id);
    notificationTimers.set(
        notification.id,
        setTimeout(() => {
            removeNotification(notification.id, 'timeout');
        }, duration)
    );
};

const subscribeToNotifications = (listener: NotificationListener) => {
    notificationListeners.add(listener);
    listener(notifications);

    return () => {
        notificationListeners.delete(listener);
    };
};

/**
 * Show a notification
 *
 * @param settings The notification settings
 */
export function showNotification(settings: Partial<NotificationSettings>) {
    const notification: NotificationItem = {
        id: settings.id ?? createNotificationId(),
        type: settings.type ?? 'default',
        container: settings.container ?? 'top-right',
        ...settings
    };

    const existingNotificationIndex = notifications.findIndex((item) => item.id === notification.id);

    if (existingNotificationIndex !== -1) {
        clearNotificationTimer(notification.id);
        notifications = notifications.map((item, index) => (index === existingNotificationIndex ? notification : item));
    } else {
        notifications = notification.insert === 'bottom' ? [...notifications, notification] : [notification, ...notifications];
    }

    emitNotifications();
    setNotificationTimer(notification);

    return notification.id;
}

export function showInfoNotification(settings: Partial<NotificationSettings>) {
    return showNotification({
        type: 'default',
        dismiss: {
            duration: 5000
        },
        ...settings
    });
}

export function showSuccessNotification(settings: Partial<NotificationSettings>) {
    return showNotification({
        type: 'success',
        dismiss: {
            duration: 5000
        },
        ...settings
    });
}

export function showWarningNotification(settings: Partial<NotificationSettings>) {
    return showNotification({
        type: 'warning',
        dismiss: {
            duration: 5000
        },
        ...settings
    });
}

export function showErrorNotification(settings: Partial<NotificationSettings>) {
    return showNotification({
        type: 'danger',
        dismiss: {
            duration: 5000
        },
        ...settings
    });
}

/**
 * Hide a notification
 *
 * @param id The notification id returned from showNotification
 */
export function removeNotification(id: string, removedBy = 'programmatic') {
    const notification = notifications.find((item) => item.id === id);
    if (!notification) return;

    clearNotificationTimer(id);
    notifications = notifications.filter((item) => item.id !== id);
    emitNotifications();
    notification.onRemoval?.(id, removedBy);
}

export interface NotificationToasterProps {
    type: Notification_Type;
    title?: ReactNode;
    message?: ReactNode;
    description?: ReactNode;
    duration?: number;
}

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(14px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const NotificationsContainer = styled.div`
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 2147483647;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;
    width: min(380px, calc(100vw - 32px));
    pointer-events: none;
`;

const getNotificationColor = (type: Notification_Type) => {
    switch (type) {
        case 'success':
            return '#2e9e5b';
        case 'danger':
            return '#d64545';
        case 'warning':
            return '#e8890c';
        case 'info':
        case 'default':
        default:
            return '#3b7bd1';
    }
};

const NotificationCard = styled.div<{ $clickable: boolean; $type: Notification_Type; $width?: number | string }>`
    width: ${(props) => (props.$width == null ? '100%' : typeof props.$width === 'number' ? `${props.$width}px` : props.$width)};
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border: 0;
    border-radius: 8px;
    background: ${(props) => getNotificationColor(props.$type)};
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
    color: #ffffff;
    pointer-events: auto;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
    animation: ${slideIn} 160ms ease-out;
`;

const NotificationContent = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
`;

const NotificationTitle = styled.div`
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
`;

const NotificationMessage = styled.div`
    color: rgba(255, 255, 255, 0.87);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.45;
    overflow-wrap: anywhere;

    a {
        color: #ffffff;
        font-weight: 600;
    }
`;

const NotificationClose = styled.button`
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    font-family: inherit;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: #ffffff;
    }
`;

const NotificationRenderer: FC<{ notification: NotificationItem }> = ({ notification }) => {
    const closeNotification = () => removeNotification(notification.id, 'click');
    const handleNotificationClick = notification.dismiss?.click === false ? undefined : closeNotification;
    const isClickDismissEnabled = notification.dismiss?.click !== false;

    if (notification.content) {
        return (
            <NotificationCard
                $clickable={isClickDismissEnabled}
                $type={notification.type}
                $width={notification.width}
                onClick={handleNotificationClick}
                role='status'
            >
                <NotificationContent>{notification.content}</NotificationContent>
                <NotificationClose
                    type='button'
                    aria-label='Close notification'
                    onClick={(event) => {
                        event.stopPropagation();
                        closeNotification();
                    }}
                >
                    x
                </NotificationClose>
            </NotificationCard>
        );
    }

    return (
        <NotificationCard
            $clickable={isClickDismissEnabled}
            $type={notification.type}
            $width={notification.width}
            onClick={handleNotificationClick}
            role='status'
        >
            <NotificationContent>
                {notification.title && <NotificationTitle>{notification.title}</NotificationTitle>}
                {(notification.message || notification.description) && (
                    <NotificationMessage>{notification.message ?? notification.description}</NotificationMessage>
                )}
            </NotificationContent>
            <NotificationClose
                type='button'
                aria-label='Close notification'
                onClick={(event) => {
                    event.stopPropagation();
                    closeNotification();
                }}
            >
                x
            </NotificationClose>
        </NotificationCard>
    );
};

export function NotificationToaster({ type, title, message, description, duration }: PropsWithChildren<NotificationToasterProps>) {
    return showNotification({
        type: type,
        title: title,
        message: message ?? description,
        dismiss: {
            duration: duration ?? 5000
        }
    });
}

export const NotificationToasterRenderer: FC = () => {
    const [items, setItems] = useState<NotificationItem[]>(notifications);

    useEffect(() => subscribeToNotifications(setItems), []);

    const topRightNotifications = items.filter((item) => item.container === 'top-right');

    return createPortal(
        <NotificationsContainer>
            {topRightNotifications.map((notification) => (
                <NotificationRenderer key={notification.id} notification={notification} />
            ))}
        </NotificationsContainer>,
        notificationsPortal
    );
};
